import React, { useState, useEffect } from 'react';
import axios from '../../services/api-axios'; // Adjust path as needed

const Leaderboard = () => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('winRate'); // 'winRate' or 'gamesPlayed'

    useEffect(() => {
        fetchLeaderboard();
    }, []);

    const fetchLeaderboard = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/rest/leaderboard/top20');
            setPlayers(response.data);
        } catch (err) {
            setError('Failed to load leaderboard');
            console.error('Error fetching leaderboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const calculateWinRate = (player) => {
        if (player.gamesPlayed === 0) return 0;
        return ((player.gamesWon / player.gamesPlayed) * 100).toFixed(1);
    };

    const sortedPlayers = [...players].sort((a, b) => {
        if (sortBy === 'winRate') {
            const aRate = calculateWinRate(a);
            const bRate = calculateWinRate(b);
            if (aRate === bRate) {
                return b.gamesPlayed - a.gamesPlayed; // Secondary sort by games played
            }
            return bRate - aRate;
        } else {
            return b.gamesPlayed - a.gamesPlayed;
        }
    });

    const getRankIcon = (rank) => {
        switch (rank) {
            case 1: return '🥇';
            case 2: return '🥈';
            case 3: return '🥉';
            default: return '🏆';
        }
    };

    const getRankColor = (rank) => {
        switch (rank) {
            case 1: return '#FFD700';
            case 2: return '#C0C0C0';
            case 3: return '#CD7F32';
            default: return '#10B981';
        }
    };

    if (loading) {
        return (
            <div style={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                padding: '32px',
                border: '1px solid rgba(75, 85, 99, 0.3)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                textAlign: 'center'
            }}>
                <div style={{
                    display: 'inline-block',
                    width: '40px',
                    height: '40px',
                    border: '3px solid #10B981',
                    borderTop: '3px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <p style={{ marginTop: '16px', color: '#9CA3AF' }}>Loading leaderboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                background: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                padding: '32px',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                textAlign: 'center'
            }}>
                <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                }}>⚠️</div>
                <p style={{ color: '#EF4444', marginBottom: '16px' }}>{error}</p>
                <button
                    onClick={fetchLeaderboard}
                    style={{
                        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        color: 'white',
                        padding: '12px 24px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 35%, #0f3460 100%)',
            color: 'white',
            fontFamily: '"Inter", system-ui, sans-serif',
            position: 'relative',
            overflow: 'hidden',
        }}>
        <div style={{
            background: 'rgba(17, 24, 39, 0.8)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(75, 85, 99, 0.3)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: '24px 32px',
                borderBottom: '1px solid rgba(75, 85, 99, 0.3)',
                background: 'rgba(55, 65, 81, 0.3)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                            borderRadius: '16px',
                            padding: '12px',
                            fontSize: '24px'
                        }}>
                            🏆
                        </div>
                        <div>
                            <h2 style={{
                                fontSize: '24px',
                                fontWeight: '800',
                                margin: '0 0 4px 0',
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                color: 'white'
                            }}>
                                Leaderboard
                            </h2>
                            <p style={{
                                color: '#9CA3AF',
                                margin: 0,
                                fontSize: '14px'
                            }}>
                                Top players by performance
                            </p>
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '8px'
                    }}>
                        <button
                            onClick={() => setSortBy('winRate')}
                            style={{
                                background: sortBy === 'winRate' 
                                    ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                    : 'rgba(55, 65, 81, 0.8)',
                                border: sortBy === 'winRate' 
                                    ? 'none'
                                    : '1px solid rgba(75, 85, 99, 0.3)',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            By Win Rate
                        </button>
                        <button
                            onClick={() => setSortBy('gamesPlayed')}
                            style={{
                                background: sortBy === 'gamesPlayed' 
                                    ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                    : 'rgba(55, 65, 81, 0.8)',
                                border: sortBy === 'gamesPlayed' 
                                    ? 'none'
                                    : '1px solid rgba(75, 85, 99, 0.3)',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            By Games Played
                        </button>
                        <button
                            onClick={fetchLeaderboard}
                            style={{
                                background: 'rgba(55, 65, 81, 0.8)',
                                border: '1px solid rgba(75, 85, 99, 0.3)',
                                borderRadius: '8px',
                                color: 'white',
                                padding: '8px 12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            🔄
                        </button>
                    </div>
                </div>
            </div>

            {/* Leaderboard List */}
            <div style={{
                maxHeight: '600px',
                overflowY: 'auto'
            }}>
                {sortedPlayers.length === 0 ? (
                    <div style={{
                        padding: '48px 32px',
                        textAlign: 'center',
                        color: '#9CA3AF'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '16px'
                        }}>🎮</div>
                        <p>No players found. Start playing to appear on the leaderboard!</p>
                    </div>
                ) : (
                    sortedPlayers.map((player, index) => {
                        const rank = index + 1;
                        const winRate = calculateWinRate(player);
                        const isTopThree = rank <= 3;
                        
                        return (
                            <div
                                key={player.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '20px 32px',
                                    borderBottom: index < sortedPlayers.length - 1 
                                        ? '1px solid rgba(75, 85, 99, 0.3)' 
                                        : 'none',
                                    background: isTopThree 
                                        ? `linear-gradient(90deg, rgba(${getRankColor(rank).replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1) 0%, transparent 100%)`
                                        : 'transparent',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = isTopThree 
                                        ? `linear-gradient(90deg, rgba(${getRankColor(rank).replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.2) 0%, transparent 100%)`
                                        : 'rgba(55, 65, 81, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = isTopThree 
                                        ? `linear-gradient(90deg, rgba(${getRankColor(rank).replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1) 0%, transparent 100%)`
                                        : 'transparent';
                                }}
                            >
                                {/* Rank */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: '80px',
                                    gap: '12px'
                                }}>
                                    <div style={{
                                        fontSize: '24px'
                                    }}>
                                        {getRankIcon(rank)}
                                    </div>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: '700',
                                        color: isTopThree ? getRankColor(rank) : '#E5E7EB'
                                    }}>
                                        #{rank}
                                    </div>
                                </div>

                                {/* Player Info */}
                                <div style={{
                                    flex: 1,
                                    marginLeft: '16px'
                                }}>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#E5E7EB',
                                        marginBottom: '4px'
                                    }}>
                                        {player.nickname}
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#9CA3AF'
                                    }}>
                                        {player.gameIds?.length || 0} active games
                                    </div>
                                </div>

                                {/* Stats */}
                                <div style={{
                                    display: 'flex',
                                    gap: '24px',
                                    alignItems: 'center'
                                }}>
                                    <div style={{
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            color: '#10B981'
                                        }}>
                                            {winRate}%
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#9CA3AF'
                                        }}>
                                            Win Rate
                                        </div>
                                    </div>
                                    <div style={{
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            color: '#E5E7EB'
                                        }}>
                                            {player.gamesPlayed}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#9CA3AF'
                                        }}>
                                            Games
                                        </div>
                                    </div>
                                    <div style={{
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            color: '#10B981'
                                        }}>
                                            {player.gamesWon}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#9CA3AF'
                                        }}>
                                            Wins
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                /* Custom scrollbar */
                div::-webkit-scrollbar {
                    width: 8px;
                }
                
                div::-webkit-scrollbar-track {
                    background: rgba(55, 65, 81, 0.3);
                    border-radius: 4px;
                }
                
                div::-webkit-scrollbar-thumb {
                    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    border-radius: 4px;
                }
                
                div::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(135deg, #059669 0%, #047857 100%);
                }
            `}</style>
        </div>
        </div>
    );
};

export default Leaderboard;